// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@adl/App/Components/Notify";
import { loadAllAccountInquiry } from "@adl/App/Handlers/AccountInquiry/AccountInquiry";
import { getErpSystemStatus } from "@adl/App/Store/Selectors/CartSelectors";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    // <XNG-Changes>
    const cart = getCurrentCartState(state).value;
    const { erpSystemIsDown, erpSystemIsDownMessage } = getErpSystemStatus(state, cart?.id);
    // </XNG-Changes>
    return {
        cart,
        // XNG-Changes
        erpSystemIsDown,
        erpSystemIsDownMessage,
    };
};

// <XNG-Changes>
const mapDispatchToProps = {
    loadAllAccountInquiry,
};
// </XNG-Changes>

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = cartApproverMessageStyles;
const minutes = 5;
const cookieName = "cardHold";
const CartApproverMessage = ({ cart, erpSystemIsDown, erpSystemIsDownMessage, loadAllAccountInquiry }: Props) => {
    // <XNG-Changes>
    const [cardHoldMessage, setCardHoldMessage] = React.useState("");

    const getCardHoldMessage = (arCreditAvail?: number) => {
        const shouldShowSoftHoldMessage =
            arCreditAvail !== undefined && (arCreditAvail <= 0 || arCreditAvail < (cart?.orderGrandTotal || 0));
        const shouldShowHardHoldMessage = cart?.properties.displayHardHoldMessage?.toLowerCase() === "true";
        const hardHoldMessage = shouldShowHardHoldMessage ? cart?.properties.hardHoldMessage || "" : "";
        const softHoldMessage = shouldShowSoftHoldMessage ? cart?.properties.softHoldMessage || "" : "";
        return hardHoldMessage || softHoldMessage;
    };

    const requestAccountInquery = () => {
        if (getCookie(cookieName)) {
            setCardHoldMessage(getCookie(cookieName) || "");
            return;
        }
        loadAllAccountInquiry({
            asOfDate: convertDateToDateOnlyString(new Date()),
            onSuccess: result => {
                let arCreditAvail;
                if (result.arCreditAvail) {
                    arCreditAvail = +result.arCreditAvail.replace("$", "").replace(",", "");
                }
                const message = getCardHoldMessage(arCreditAvail);
                setCardHoldMessage(message);
                const currentDate = new Date();
                setCookie(cookieName, message, {
                    expires: new Date(currentDate.setMinutes(currentDate.getMinutes() + minutes)),
                });
            },
        });
    };
    const handleCardHoldMessageDisplay = () => {
        const accountInquiryInterval = setInterval(() => {
            requestAccountInquery();
        }, minutes * 60 * 1000);

        // for first page load
        requestAccountInquery();
        return accountInquiryInterval;
    };
    React.useEffect(() => {
        if (!cart) {
            return;
        }
        const accountInquiryInterval = handleCardHoldMessageDisplay();
        return () => {
            clearInterval(accountInquiryInterval);
        };
    }, []);
    React.useEffect(() => {
        if (!cart) {
            return;
        }
        const accountInquiryInterval = handleCardHoldMessageDisplay();
        return () => {
            clearInterval(accountInquiryInterval);
        };
    }, [cart]);
    // </XNG-Changes>

    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    const isAwaitingApproval = cart.isAwaitingApproval;
    const cartNotPriced = cart.cartNotPriced;
    const hasInsufficientInventory = cart.hasInsufficientInventory && cart.fulfillmentMethod === FulfillmentMethod.Ship;
    const hasInvalidPrice = hasProductsWithInvalidPrice(cart);
    const failedToGetRealTimeInventory = cart.failedToGetRealTimeInventory;
    const productsCannotBePurchased = cart.cartLines?.some(o => o.isRestricted || !o.isActive);
    const hasOutOfStockCartLines = cart.cartLines?.some(o => o.availability?.messageType === 2 && o.canBackOrder);

    return (
        <StyledWrapper {...styles.approvingWrapper}>
            {/* <Typography {...styles.approvingText} data-test-selector="cart_approvingOrderMessage">
                {translate("Approving Order for {0}", cart.initiatedByUserName?.toUpperCase())}
            </Typography> */}
            {/* <XNG-Changes> */}
            {cardHoldMessage && <Notify type="warning">{cardHoldMessage}</Notify>}
            {hasOutOfStockCartLines && <Notify type="warning">{siteMessage("Custom_Some_Items_OutOfStock")}</Notify>}
            {/* </XNG-Changes> */}
            {isAwaitingApproval && (
                <Notify type="info">
                    {translate("Approving Order for {0}", cart.initiatedByUserName?.toUpperCase())}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify type="warning">{siteMessage("Cart_ProductsCannotBePurchased")}</Notify>
            )}
            {cartNotPriced && <Notify type="warning">{siteMessage("Cart_NoPriceAvailableAtCheckout")}</Notify>}
            {hasInsufficientInventory && (
                <Notify type="warning">{siteMessage("Cart_InsufficientInventoryAtCheckout")}</Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify type="warning">{siteMessage("Cart_InvalidPriceAtCheckout")}</Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify type="warning">{siteMessage("Cart_NoInventoryAvailableAtCheckout")}</Notify>
            )}
            {erpSystemIsDown && <Notify type="warning">{parse(erpSystemIsDownMessage, parserOptions)}</Notify>}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
